import {Component, Input, ViewChild} from '@angular/core';
import {map, of, switchMap, tap} from "rxjs";
import { CourseFilterComponent } from 'src/app/utils/course-filter/course-filter.component';
import {ModalComponent} from "../modal/modal.component";
import { ProductContextService } from 'src/app/col/services/product-context.service';
import {NativeServiceApiProvider} from "../../services/native-api-provider.service";
import {AuthorizationServiceProvider} from "../../auth_profile/services/authorization-service.provider";
import * as LessonsV3 from "../../col/model/v3/lessons";

@Component({
    selector: 'app-activate-course-modal',
    templateUrl: './activate-course-modal.component.html',
    styleUrls: ['./activate-course-modal.component.scss'],
    standalone: false
})
export class ActivateCourseModalComponent {
  private _studentId: number;
  private _schoolId: number;
  private _lang: string;
  isActivatingInProgress = false
  CourseFilterComponent = CourseFilterComponent
  private productContext: LessonsV3.ProductContextDetails | null = null

  @Input() defaultContent = true
  @Input() onlyButton = false
  @Input() isHidden = false
  @Input()
  set studentId(value: number) {
    this._studentId = value;
    this.getProductContext().subscribe()
  }
  get studentId() {
    return this._studentId;
  }
  @Input()
  public set langCode(lang: string) {
    this._lang = lang;
    this.getProductContext().subscribe()
  }
  public get langCode() {
    return this._lang
  }
  @Input()
  set contextUpdate(context: LessonsV3.ProductContextDetails | null) {
    this.productContext = context
  }
  @ViewChild("activateCourseModal", {static: true}) activateCourseModal: ModalComponent

  constructor(
    private productContextService: ProductContextService,
    private provider: NativeServiceApiProvider,
    private auth: AuthorizationServiceProvider
  ) {}

  activateCourse() {
    this.isActivatingInProgress = true
    this.auth.getAuthDetailsService().pipe(
      switchMap((authApi) => authApi.getSelfSchoolId()),
      tap(schoolId => this._schoolId = schoolId),
      switchMap((schoolId) => this.provider.colV3().pipe(
        switchMap((api) => api.updateProductContext(schoolId, this.studentId, this.langCode, {
          productCode: this.langCode,
          nextLessonType: LessonsV3.LessonType.Prova,
          productVersion: null,
        }))
      )),
      switchMap(() => this.getProductContext()),
      switchMap(_ => this.provider.colV3().pipe(
        switchMap((api) => api.createStudentBundle(this._schoolId, this.studentId, {
          product: { code: this.langCode },
          date: new Date(),
          available: 1,
          expiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
          registeredLesson: 1,
          name: 'Free Welcome Credit',
          price: 'free',
          bundleDuration: null,
          note: '',
        }))
      )),
    ).subscribe(_ => {
      this.isActivatingInProgress = false
      this.productContextService.productContextUpdated()
      this.activateCourseModal.hide()
    })
  }

  getLanguageTranslation(langCode: string) {
    if(!langCode) return ''
    let lang = CourseFilterComponent.getFiltersShort().find(l => l.filter == langCode)
    if(!lang) return ''
    return "EBOOKS.FILTER_LANGS." + lang.translationCode
  }

  getProductContext() {
    if (!this.studentId || !this.langCode)
      return of(null);

    return this.auth.getAuthDetailsService().pipe(
      switchMap((authApi) => authApi.getSelfSchoolId()),
      switchMap((schoolId) =>
        this.provider.colV3().pipe(
          switchMap((api) =>
            api.getProductContexts(schoolId, this.studentId, this.langCode)
          ),
          map((contexts) => contexts?.[0] ?? null)
        )
      ),
      tap(productContext => this.productContext = productContext)
    )
  }

  isCourseActive() {
    return !!this.productContext
  }

  /**
   * There is nothing to activate unless a concrete course is selected. The course filters use a
   * blank code as their "all courses" option, and for that option no product context is ever
   * loaded - so relying on isCourseActive() alone would show the activation button.
   */
  isActivationAvailable() {
    return !!this.langCode?.trim() && !this.isCourseActive()
  }
}
